import axios from 'axios';
import { parseErrorMessage } from '@/utils/errorParser';
import { PagedResponse } from '@/types/api';
import { API_ENDPOINTS } from './types';

/**
 * Identity/Tenant related types
 */

export interface UserTenantDTO {
  id: number;
  keycloakUserId: string;
  tenantId: string;
  tenantName: string;
  role: 'OWNER' | 'ADMIN' | 'MEMBER' | 'TENANT_VIEWER';
  email?: string;
  displayName?: string;
  isDefault: boolean;
  invitationStatus?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING';
  joinedAt?: number;
  createdAt: string;
  updatedAt: string;
}

export interface TenantDTO {
  id: number;
  tenantId: string;
  name: string;
  description?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING';
  keycloakRealm?: string;
  keycloakClientId?: string;
  metadata?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface CurrentUserContextDTO {
  keycloakUserId: string;
  email?: string;
  displayName?: string;
  currentTenant?: UserTenantDTO;
  tenants: UserTenantDTO[];
  requiresOnboarding: boolean;
  realmRoles?: string[];
  platformAdmin: boolean;
}

export interface TenantOnboardingRequest {
  organizationName: string;
  description?: string;
  industry?: string;
  companySize?: string;
  primaryUseCase?: string;
  metadata?: string;
}

export interface TenantOnboardingResponse {
  tenant: TenantDTO;
  userTenant: UserTenantDTO;
  isNewTenant: boolean;
  message: string;
}

export interface TenantUpdateRequest {
  name?: string;
  description?: string;
  metadata?: string;
}

export interface AdminCreateTenantRequest {
  organizationName: string;
  description?: string;
  industry?: string;
  companySize?: string;
  primaryUseCase?: string;
  ownerEmail?: string;
  ownerDisplayName?: string;
  status?: 'ACTIVE' | 'PENDING';
}

export interface AddMemberResponse {
  member: UserTenantDTO;
  newUserCreated: boolean;
  temporaryPassword: string | null;
}

// Admin API DTOs
export interface AdminUserDTO {
  keycloakUserId: string;
  email: string | null;
  displayName: string | null;
  tenantMemberships: UserTenantDTO[];
  tenantCount: number;
  lastActivity: string;
  createdAt: string;
}

export interface TenantStatsDTO {
  tenantId: string;
  tenantName: string;
  memberCount: number;
  workflowCount: number;
  lastActivity: string;
}

export interface PlatformStatsDTO {
  totalTenants: number;
  activeTenants: number;
  suspendedTenants: number;
  totalUsers: number;
  activeUsersLast30Days: number;
}

export interface EmailAuditLogDTO {
  id: number;
  recipient: string;
  subject: string;
  templateName: string | null;
  status: 'SENT' | 'FAILED' | 'PENDING';
  provider: string;
  errorMessage: string | null;
  retryable: boolean;
  sentAt: string;
}

// API endpoint configuration
const IDENTITY_API_BASE = API_ENDPOINTS.IDENTITY;

/**
 * Identity API service for user context and tenant management
 */
class IdentityApiService {
  private getHeaders(keycloakUserId: string, email?: string, displayName?: string) {
    // Note: Authorization header is added by axios interceptor
    // This only adds the custom identity headers
    const headers: Record<string, string> = {
      'X-Keycloak-User-Id': keycloakUserId,
    };
    if (email) {
      headers['X-User-Email'] = email;
    }
    if (displayName) {
      headers['X-User-Display-Name'] = displayName;
    }
    return headers;
  }

  /**
   * Get the current user's context including tenant memberships.
   * This determines if onboarding is required.
   */
  async getCurrentUserContext(
    keycloakUserId: string,
    email?: string,
    displayName?: string
  ): Promise<CurrentUserContextDTO> {
    try {
      const response = await axios.get<CurrentUserContextDTO>(
        `${IDENTITY_API_BASE}/users/me/context`,
        {
          headers: this.getHeaders(keycloakUserId, email, displayName),
          timeout: 10000,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get user context:', error);
      throw new Error(
        `Failed to get user context: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Onboard a new user by creating their organization/tenant
   */
  async onboardUser(
    keycloakUserId: string,
    email: string | undefined,
    displayName: string | undefined,
    request: TenantOnboardingRequest
  ): Promise<TenantOnboardingResponse> {
    try {
      const response = await axios.post<TenantOnboardingResponse>(
        `${IDENTITY_API_BASE}/users/me/onboard`,
        request,
        {
          headers: this.getHeaders(keycloakUserId, email, displayName),
          timeout: 15000,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to onboard user:', error);
      throw new Error(
        `Failed to onboard user: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get all tenant memberships for the current user
   */
  async getUserTenants(keycloakUserId: string): Promise<UserTenantDTO[]> {
    try {
      const response = await axios.get<UserTenantDTO[]>(
        `${IDENTITY_API_BASE}/users/me/tenants`,
        {
          headers: this.getHeaders(keycloakUserId),
          timeout: 10000,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get user tenants:', error);
      throw new Error(
        `Failed to get user tenants: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Switch the current user's active tenant
   */
  async switchTenant(keycloakUserId: string, tenantId: string): Promise<UserTenantDTO> {
    try {
      const response = await axios.post<UserTenantDTO>(
        `${IDENTITY_API_BASE}/users/me/tenants/${encodeURIComponent(tenantId)}/switch`,
        {},
        {
          headers: this.getHeaders(keycloakUserId),
          timeout: 10000,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to switch tenant:', error);
      throw new Error(
        `Failed to switch tenant: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Leave a tenant (remove self from membership)
   */
  async leaveTenant(keycloakUserId: string, tenantId: string): Promise<void> {
    try {
      await axios.delete(
        `${IDENTITY_API_BASE}/users/me/tenants/${encodeURIComponent(tenantId)}`,
        {
          headers: this.getHeaders(keycloakUserId),
          timeout: 10000,
        }
      );
    } catch (error) {
      console.error('Failed to leave tenant:', error);
      throw new Error(
        `Failed to leave tenant: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get tenant information
   */
  async getTenant(tenantId: string): Promise<TenantDTO> {
    try {
      const response = await axios.get<TenantDTO>(
        `${IDENTITY_API_BASE}/tenants/${encodeURIComponent(tenantId)}`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get tenant:', error);
      throw new Error(
        `Failed to get tenant: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get all members of a tenant
   */
  async getTenantMembers(tenantId: string): Promise<UserTenantDTO[]> {
    try {
      const response = await axios.get<UserTenantDTO[]>(
        `${IDENTITY_API_BASE}/tenants/${encodeURIComponent(tenantId)}/members`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get tenant members:', error);
      throw new Error(
        `Failed to get tenant members: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Update tenant information
   */
  async updateTenant(tenantId: string, request: TenantUpdateRequest): Promise<TenantDTO> {
    try {
      const response = await axios.put<TenantDTO>(
        `${IDENTITY_API_BASE}/tenants/${encodeURIComponent(tenantId)}`,
        request,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to update tenant:', error);
      throw new Error(
        `Failed to update tenant: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Add a member to a tenant
   */
  async addTenantMember(
    tenantId: string,
    request: {
      keycloakUserId?: string; // Optional - will be auto-created if not provided
      email: string;
      displayName: string;
      role: 'OWNER' | 'ADMIN' | 'MEMBER' | 'TENANT_VIEWER';
    }
  ): Promise<AddMemberResponse> {
    try {
      const response = await axios.post<AddMemberResponse>(
        `${IDENTITY_API_BASE}/tenants/${encodeURIComponent(tenantId)}/members`,
        request,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to add tenant member:', error);
      throw new Error(parseErrorMessage(error, 'Failed to add tenant member'));
    }
  }

  /**
   * Update a member's role in a tenant
   */
  async updateMemberRole(
    tenantId: string,
    keycloakUserId: string,
    role: 'OWNER' | 'ADMIN' | 'MEMBER' | 'TENANT_VIEWER'
  ): Promise<UserTenantDTO> {
    try {
      const response = await axios.put<UserTenantDTO>(
        `${IDENTITY_API_BASE}/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(keycloakUserId)}/role`,
        { role },
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to update member role:', error);
      throw new Error(parseErrorMessage(error, 'Failed to update member role'));
    }
  }

  /**
   * Remove a member from a tenant
   */
  async removeTenantMember(tenantId: string, keycloakUserId: string): Promise<void> {
    try {
      await axios.delete(
        `${IDENTITY_API_BASE}/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(keycloakUserId)}`,
        { timeout: 10000 }
      );
    } catch (error) {
      console.error('Failed to remove tenant member:', error);
      throw new Error(parseErrorMessage(error, 'Failed to remove tenant member'));
    }
  }

  /**
   * Reset a member's password (generates new temporary password)
   */
  async resetMemberPassword(tenantId: string, keycloakUserId: string): Promise<{ temporaryPassword: string }> {
    try {
      const response = await axios.post<{ message: string; temporaryPassword: string }>(
        `${IDENTITY_API_BASE}/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(keycloakUserId)}/reset-password`,
        {},
        { timeout: 10000 }
      );
      return { temporaryPassword: response.data.temporaryPassword };
    } catch (error) {
      console.error('Failed to reset member password:', error);
      throw new Error(parseErrorMessage(error, 'Failed to reset password'));
    }
  }

  // ========================================
  // Platform Admin APIs (requires platform_admin role)
  // ========================================

  /**
   * Get all tenants (organizations) - Admin only
   */
  async adminGetAllTenants(params: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    search?: string;
    status?: string;
  } = {}): Promise<PagedResponse<TenantDTO>> {
    try {
      const response = await axios.get<PagedResponse<TenantDTO>>(
        `${IDENTITY_API_BASE}/admin/tenants`,
        {
          params: {
            page: params.page ?? 0,
            size: params.size ?? 20,
            sortBy: params.sortBy ?? 'createdAt',
            sortDir: params.sortDir ?? 'desc',
            search: params.search,
            status: params.status,
          },
          timeout: 15000,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get all tenants:', error);
      throw new Error(
        `Failed to get tenants: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get tenant by ID - Admin only
   */
  async adminGetTenantById(tenantId: string): Promise<TenantDTO> {
    try {
      const response = await axios.get<TenantDTO>(
        `${IDENTITY_API_BASE}/admin/tenants/${encodeURIComponent(tenantId)}`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get tenant:', error);
      throw new Error(
        `Failed to get tenant: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get tenant statistics - Admin only
   */
  async adminGetTenantStats(tenantId: string): Promise<TenantStatsDTO> {
    try {
      const response = await axios.get<TenantStatsDTO>(
        `${IDENTITY_API_BASE}/admin/tenants/${encodeURIComponent(tenantId)}/stats`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get tenant stats:', error);
      throw new Error(
        `Failed to get tenant stats: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get all users - Admin only
   */
  async adminGetAllUsers(params: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    search?: string;
    role?: string;
  } = {}): Promise<PagedResponse<AdminUserDTO>> {
    try {
      const response = await axios.get<PagedResponse<AdminUserDTO>>(
        `${IDENTITY_API_BASE}/admin/users`,
        {
          params: {
            page: params.page ?? 0,
            size: params.size ?? 20,
            sortBy: params.sortBy ?? 'createdAt',
            sortDir: params.sortDir ?? 'desc',
            search: params.search,
            role: params.role,
          },
          timeout: 15000,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get all users:', error);
      throw new Error(
        `Failed to get users: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get user by keycloak ID - Admin only
   */
  async adminGetUserById(keycloakUserId: string): Promise<AdminUserDTO> {
    try {
      const response = await axios.get<AdminUserDTO>(
        `${IDENTITY_API_BASE}/admin/users/${encodeURIComponent(keycloakUserId)}`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get user:', error);
      throw new Error(
        `Failed to get user: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get platform-wide statistics - Admin only
   */
  async adminGetPlatformStats(): Promise<PlatformStatsDTO> {
    try {
      const response = await axios.get<PlatformStatsDTO>(
        `${IDENTITY_API_BASE}/admin/stats`,
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get platform stats:', error);
      throw new Error(
        `Failed to get platform stats: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Suspend a tenant - Admin only
   */
  async adminSuspendTenant(tenantId: string): Promise<TenantDTO> {
    try {
      const response = await axios.post<TenantDTO>(
        `${IDENTITY_API_BASE}/admin/tenants/${encodeURIComponent(tenantId)}/suspend`,
        {},
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to suspend tenant:', error);
      throw new Error(
        `Failed to suspend tenant: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Activate a tenant - Admin only
   */
  async adminActivateTenant(tenantId: string): Promise<TenantDTO> {
    try {
      const response = await axios.post<TenantDTO>(
        `${IDENTITY_API_BASE}/admin/tenants/${encodeURIComponent(tenantId)}/activate`,
        {},
        { timeout: 10000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to activate tenant:', error);
      throw new Error(
        `Failed to activate tenant: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Create a new organization/tenant - Admin only
   */
  async adminCreateTenant(request: AdminCreateTenantRequest): Promise<TenantDTO> {
    try {
      const response = await axios.post<TenantDTO>(
        `${IDENTITY_API_BASE}/admin/tenants`,
        request,
        { timeout: 15000 }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to create tenant:', error);
      throw new Error(
        `Failed to create organization: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get email audit logs - Admin only
   */
  async adminGetEmailAuditLogs(params: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    recipient?: string;
    status?: string;
    templateName?: string;
    from?: string;
    to?: string;
  } = {}): Promise<PagedResponse<EmailAuditLogDTO>> {
    try {
      const response = await axios.get<PagedResponse<EmailAuditLogDTO>>(
        `${IDENTITY_API_BASE}/admin/email-audit-logs`,
        {
          params: {
            page: params.page ?? 0,
            size: params.size ?? 20,
            sortBy: params.sortBy ?? 'sentAt',
            sortDir: params.sortDir ?? 'desc',
            recipient: params.recipient,
            status: params.status,
            templateName: params.templateName,
            from: params.from,
            to: params.to,
          },
          timeout: 15000,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get email audit logs:', error);
      throw new Error(
        `Failed to get email audit logs: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Retry sending an email by audit log ID - Admin only
   */
  async adminRetryEmail(id: number): Promise<void> {
    try {
      await axios.post(
        `${IDENTITY_API_BASE}/admin/email-audit-logs/${id}/retry`,
        {},
        { timeout: 15000 }
      );
    } catch (error) {
      console.error('Failed to retry email:', error);
      throw new Error(
        `Failed to retry email: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }
}

export const identityApi = new IdentityApiService();
export default identityApi;
